import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import MDTypography from "ui/MDTypography";
import MDBox from "ui/MDBox";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { styled, keyframes } from "@mui/material/styles";
import meetingServices from "services/meeting-services";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const CardContainer = styled(MDBox)(({ theme }) => ({
  width: "90%",
  maxWidth: 450,
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[6],
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  animation: `${fadeIn} 0.6s ease-out`,
}));

const StatusIcon = styled("div")(({ theme, status }) => ({
  marginBottom: theme.spacing(2),
  svg: {
    fontSize: 80,
    color:
      status === "success"
        ? theme.palette.success.main
        : status === "error"
          ? theme.palette.error.main
          : theme.palette.text.primary,
  },
}));

const VerifyAttendance = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Verifying your attendance...");
  const [details, setDetails] = useState({});

  useEffect(() => {
    const id = searchParams.get("id");
    if (!id) {
      setStatus("error");
      setMessage("❌ Invalid or missing meeting ID.");
      return;
    }

    meetingServices.verifyKarkunAttendance(id)
      .then((res) => {
        const data = res?.data || res;
        if (data?.success) {
          setStatus("success");
          setMessage(data.message);
          setDetails({
            karkunName: data.karkunName,
            meetingTitle: data.meetingTitle,
          });
        } else {
          setStatus("error");
          setMessage(data.message || "❌ Failed to mark attendance.");
        }
      })
      .catch((err) => {
        console.error("Attendance verification error:", err);
        const backendMessage =
          err?.response?.data?.message || err?.message || "❌ Unknown error.";
        setStatus("error");
        setMessage(backendMessage);
      });

  }, [searchParams]);

  return (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      sx={{ backgroundColor: "#f5f7fa" }}
    >
      <CardContainer>
        <StatusIcon status={status}>
          {status === "loading" && <CircularProgress size={80} />}
          {status === "success" && <CheckCircleIcon />}
          {status === "error" && <ErrorIcon />}
        </StatusIcon>

        <MDTypography
          variant={status === "success" ? "h4" : "h5"}
          fontWeight="medium"
          textAlign="center"
          gutterBottom
        >
          {message}
        </MDTypography>

        {status === "success" && details.karkunName && (
          <MDTypography variant="h6" color="textSecondary" textAlign="center">
            {details.karkunName} <br /> Meeting: {details.meetingTitle}
          </MDTypography>
        )}
      </CardContainer>
    </MDBox>
  );
};

export default VerifyAttendance;
